import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Line } from 'react-chartjs-2';
import { TrendUp } from '@phosphor-icons/react';
import { AcquisitionYearBucket } from '../types/statistics.types';
import { COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionYearBucket[];
  loading: boolean;
}

export const YearlyYieldChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    return {
      labels: data.map((d) => String(d.year)),
      datasets: [
        {
          type: 'line' as const,
          label: 'Volume-gewogen yield',
          data: data.map((d) =>
            d.volumeWeightedDealYield > 0
              ? +(d.volumeWeightedDealYield * 100).toFixed(2)
              : null,
          ),
          borderColor: '#5B7304',
          backgroundColor: '#5B730433',
          tension: 0.3,
          spanGaps: true,
          pointRadius: 5,
          pointBackgroundColor: '#5B7304',
        },
        {
          type: 'line' as const,
          label: 'Gemiddelde yield',
          data: data.map((d) =>
            d.avgDealYield > 0 ? +(d.avgDealYield * 100).toFixed(2) : null,
          ),
          borderColor: '#EA8162',
          backgroundColor: '#EA816233',
          borderDash: [6, 4],
          tension: 0.3,
          spanGaps: true,
          pointRadius: 4,
          pointBackgroundColor: '#EA8162',
        },
      ],
      _meta: data,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <TrendUp className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TrendUp className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Yield evolutie per jaar</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>Hoe het rendement van gewonnen deals evolueert over de jaren.</p>
                <p>
                  <strong>Gemiddelde yield:</strong> rekenkundig gemiddelde van{' '}
                  <code>bidPriceActualNetYield</code> over deals (Deal!) van dat jaar.
                </p>
                <p>
                  <strong>Volume-gewogen yield:</strong> Σ(yield × bod) / Σ(bod) —
                  weegt grote transacties zwaarder. Dit is de meest representatieve
                  maatstaf voor het bestuur.
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Op basis van gewonnen deals (acquisitiephase = "Deal!")
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {chartData ? (
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { boxWidth: 12, padding: 12, font: { size: 12 } },
                  },
                  tooltip: {
                    ...COMMON_TOOLTIP,
                    callbacks: {
                      label: (ctx: any) =>
                        ctx.parsed.y === null
                          ? `${ctx.dataset.label}: geen data`
                          : `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}%`,
                      afterBody: (items: any[]) => {
                        if (!items.length) return [];
                        const idx = items[0].dataIndex;
                        const meta = chartData._meta[idx];
                        return [`Aantal deals: ${meta.deals}`];
                      },
                    },
                  },
                },
                scales: {
                  x: { grid: { display: false } },
                  y: {
                    beginAtZero: true,
                    ticks: { callback: (v: any) => `${v}%` },
                    grid: { color: 'rgba(49, 64, 59, 0.1)' },
                    title: { display: true, text: 'Netto yield (%)' },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
